import * as Sentry from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { type CSSProperties, lazy, useEffect } from "react";
import { Toaster } from "sonner";

import { EnvBanner } from "@/components/layout/env-banner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useDismissToastsOnEscape } from "@/lib/notify";

/** Toast theme (TECH-1351). "dark" = Linear-style, like the batch-action island; both
 *  looks live in globals.css. */
const TOAST_THEME: "light" | "dark" = "dark";

interface RouterContext {
  queryClient: QueryClient;
}

const RouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  useDismissToastsOnEscape();

  return (
    // delay={0}: tooltips show immediately on hover (user requirement).
    <TooltipProvider delay={0}>
      <EnvBanner />
      <Outlet />
      {/* Bottom-right, theme TOAST_THEME (look in globals.css): top-right covered the
          « Actions » menu (« Choisir la prochaine étape ») of the claim drawer (TECH-1351).
          The toast region is click-through (globals.css), so it never blocks the UI
          underneath. */}
      <Toaster
        position="bottom-right"
        style={{ "--width": "26rem" } as CSSProperties}
        theme={TOAST_THEME}
        expand
        closeButton
      />
      <RouterDevtools position="bottom-right" />
    </TooltipProvider>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground">Cette page n'existe pas.</p>
      <Link to="/claims" className="text-sm font-medium underline underline-offset-4">
        Retour aux dossiers
      </Link>
    </div>
  );
}

function ErrorComponent({ error }: { error: Error }) {
  Sentry.captureException(error);

  useEffect(() => {
    if (import.meta.env.DEV) {
      void import("@/dev/report-client-errors").then(({ reportClientError }) => {
        reportClientError({ kind: "error-boundary", message: error.message, stack: error.stack });
      });
    }
  }, [error]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4">
      <h1 className="text-2xl font-semibold">Une erreur est survenue</h1>
      <p className="max-w-lg text-sm text-muted-foreground">{error.message}</p>
      <button
        type="button"
        onClick={() => window.location.assign("/")}
        className="text-sm font-medium underline underline-offset-4"
      >
        Recharger l'application
      </button>
    </div>
  );
}
